import { memo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as EmailValidator from 'email-validator';

import { Icons, IconType } from '../../../../constants/icons';
import { DefaultColors } from '../../../../constants/Theme/DefaultColors';
import { CarouselItem } from '../../../../models/Base';
import {
  IconInfoCell,
  JoyajoyImage,
  TextInput,
  TextInputProps,
} from '../../../Molecules';

import './ClubRedeemRewardOfferPaymentTemplate.css';

export type ClubRedeemRewardOfferPaymentTemplateProps = {
  defaultEmail: string;
  clubImage: string;
  clubName: string;
  offerCarousel: CarouselItem[];
  offerName: string;
  offerDesciption: string;
  usd: number;
  cost?: number;
  emailEditable: boolean;
  onPay: (email: string, withStripe: boolean) => void;
};

export const ClubRedeemRewardOfferPaymentTemplate = memo(
  ({
    defaultEmail,
    clubImage,
    clubName,
    offerCarousel,
    offerName,
    offerDesciption,
    usd,
    cost,
    emailEditable,
    onPay,
  }: ClubRedeemRewardOfferPaymentTemplateProps) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState(defaultEmail);
    const [retypeEmail, setRetypeEmail] = useState('');
    const [agree, setAgree] = useState(false);

    const updateEmail = useCallback<NonNullable<TextInputProps['onChange']>>(
      ({ target }) => {
        setEmail(target.value);
      },
      [],
    );
    const updateRetypeEmail = useCallback<
      NonNullable<TextInputProps['onChange']>
    >(({ target }) => {
      setRetypeEmail(target.value);
    }, []);

    const canPay =
      EmailValidator.validate(email) &&
      (!emailEditable || email == retypeEmail) &&
      agree;

    const onClickWithStripe = useCallback(() => {
      if (canPay) {
        onPay(email, true);
      }
    }, [canPay, email, onPay]);
    const onClickWithCredit = useCallback(() => {
      if (canPay) {
        onPay(email, false);
      }
    }, [canPay, email, onPay]);

    return (
      <div className="club-redeem-payment">
        <JoyajoyImage
          resource={{ uri: clubImage }}
          className="club-image rounded-lg"
        />
        <div className="club-name">{clubName}</div>
        <div className="offer-info">
          <IconInfoCell
            title={offerName}
            icon={
              offerCarousel[0]?.image?.publicUrls?.medium ??
              Icons[IconType.ClassroomLine]
            }
            description={offerDesciption}
          />
        </div>
        <div className="text">
          {t(
            'If there is no JOYAJOY account with the following email, a new account will be created for you.',
          )}
        </div>
        {emailEditable ? (
          <div className="input">
            <TextInput
              value={email}
              onChange={updateEmail}
              placeholder={t('Email')}
            />
            <TextInput
              value={retypeEmail}
              onChange={updateRetypeEmail}
              placeholder={t('Retype Email')}
            />
          </div>
        ) : (
          <div className="email">{`${t('Email: ')} ${email}`}</div>
        )}
        <div className="purchase-description">
          <p>{t('Purchase membership description 1')}</p>
          <p>{t('Purchase membership description 2')}</p>
        </div>
        <hr />
        <div className="agreement">
          <img
            src={agree ? Icons[IconType.Tick] : Icons[IconType.EmptyTick]}
            className="icon"
            onClick={() => setAgree((c) => !c)}
          />
          <label htmlFor="accept-policy">
            <Trans i18nKey="Accept Terms">
              I agree to JOYAJOY&apos;s
              <a
                href="https://joyajoy.com/zh/pages/term-of-services"
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions
              </a>
              and
              <a
                href="https://joyajoy.com/zh/pages/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Statement
              </a>
            </Trans>
          </label>
        </div>
        <button
          className="next-btn"
          onClick={onClickWithStripe}
          disabled={!canPay}
          style={{
            backgroundColor: canPay
              ? DefaultColors.cyan.DIPDIP_COLOR
              : DefaultColors.gray.BRIGHT_GRAY,
          }}
        >
          {t('Pay with Stripe: ') + `US$${usd}`}
        </button>
        {cost != undefined && (
          <button
            className="next-btn"
            onClick={onClickWithCredit}
            disabled={!canPay}
            style={{
              backgroundColor: canPay
                ? DefaultColors.cyan.DIPDIP_COLOR
                : DefaultColors.gray.BRIGHT_GRAY,
            }}
          >
            <img src={Icons[IconType.Credit]} className="icon" />
            {`${cost}`}
          </button>
        )}
      </div>
    );
  },
);
